import { StudioService } from "../../application/services/studio-service.js";
import type { StudioVersion } from "../../studio/studio-version.js";
import { StudioVersionStatus } from "../../studio/studio-version.js";
import type { JsonObject } from "../../shared/kernel/json.js";

type StateObserver = (state: JsonObject, status: 'IDLE' | 'SAVING' | 'SAVED' | 'ERROR') => void;

export class EditorStateManager {
  private history: JsonObject[] = [];
  private historyIndex: number = -1;
  private currentConfig: JsonObject;
  private saveStatus: 'IDLE' | 'SAVING' | 'SAVED' | 'ERROR' = 'IDLE';
  private observers: Set<StateObserver> = new Set();
  
  private saveTimeout: any = null;
  private readonly DEBOUNCE_MS = 1500;

  constructor(
    private readonly studioService: StudioService,
    private readonly requestContext: any,
    private readonly definitionId: string,
    public readonly currentVersion: StudioVersion
  ) {
    this.currentConfig = JSON.parse(JSON.stringify(currentVersion.configuration));
    this.pushHistory(this.currentConfig, false);
  }

  public subscribe(observer: StateObserver): () => void {
    this.observers.add(observer);
    observer(this.currentConfig, this.saveStatus);
    return () => this.observers.delete(observer);
  }

  private notify() {
    for (const observer of this.observers) {
      observer(this.currentConfig, this.saveStatus);
    }
  }

  public updateConfig(newConfig: JsonObject) {
    if (!this.currentVersion || this.currentVersion.status !== StudioVersionStatus.Draft) {
      console.warn("Cannot mutate non-DRAFT version.");
      return;
    }
    
    // Deep clone to prevent accidental references
    const cloned = JSON.parse(JSON.stringify(newConfig));
    this.currentConfig = cloned;
    this.pushHistory(cloned, true);
    this.triggerAutosave();
  }

  private pushHistory(config: JsonObject, notify: boolean = true) {
    // If we are not at the end of history, truncate the future
    if (this.historyIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.historyIndex + 1);
    }
    this.history.push(JSON.parse(JSON.stringify(config)));
    this.historyIndex++;
    if (notify) this.notify();
  }

  public undo() {
    if (this.canUndo()) {
      this.historyIndex--;
      this.currentConfig = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
      this.triggerAutosave();
    }
  }

  public redo() {
    if (this.canRedo()) {
      this.historyIndex++;
      this.currentConfig = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
      this.triggerAutosave();
    }
  }

  public canUndo(): boolean {
    return this.historyIndex > 0;
  }

  public canRedo(): boolean {
    return this.historyIndex < this.history.length - 1;
  }

  private triggerAutosave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveStatus = 'SAVING';
    this.notify();

    this.saveTimeout = setTimeout(async () => {
      try {
        await this.studioService.updateDraft(
          this.requestContext,
          this.definitionId as any,
          this.currentVersion.id,
          this.currentConfig
        );
        this.saveStatus = 'SAVED';
      } catch (err) {
        console.error("Autosave failed", err);
        this.saveStatus = 'ERROR';
      }
      this.notify();
    }, this.DEBOUNCE_MS);
  }
}
